import { Controller } from "@hotwired/stimulus"

// "Powiadomienia na telefon" on the Today page: offers web push for this
// browser or installed web app and subscribes it through the panel service
// worker (public/panel-sw.js) with the VAPID public key from the layout's
// <meta name="web-push-public-key">. The subscription goes to
// Users::PushSubscriptionsController, which binds it to the signed-in user.
//
// The card starts hidden and shows only when subscribing makes sense: push is
// supported and this device is not subscribed yet. iOS Safari has no push in
// a browser tab, only in a web app on the home screen, so there the card
// shows the "add to home screen" hint instead of the button. "Nie teraz" is
// remembered per device in localStorage; the subscription state itself is
// always read from the browser (pushManager.getSubscription), never stored.
// A device that is already subscribed is re-posted once per user, so a shared
// tablet follows whoever signed in last.
//
// data-push-support on the element reports the check — "supported",
// "unsupported" or "ios" (a browser tab on iOS); system specs read it.
export default class extends Controller {
  static targets = ["intro", "iosHint", "message", "enable", "disable"]
  static values = {
    subscribeUrl: String,
    workerUrl: { type: String, default: "/panel-sw.js" },
    user: String,
    dismissKey: { type: String, default: "calendra.push.dismissed" },
    boundKey: { type: String, default: "calendra.push.bound" },
    workingText: String,
    enabledText: String,
    deniedText: String,
    failedText: String
  }

  async connect() {
    const state = this.supported ? "supported" : (this.iosBrowserTab ? "ios" : "unsupported")
    this.element.dataset.pushSupport = state
    this.hide() // idempotent: a reconnect re-decides from scratch
    if (state === "unsupported" || this.dismissed) return
    if (state === "ios") {
      this.iosHintTarget.classList.remove("hidden")
      this.enableTarget.classList.add("hidden")
      this.show()
      return
    }
    if (!this.publicKey) return

    const subscription = await this.currentSubscription()
    if (subscription) {
      await this.rebind(subscription)
      return
    }
    if (Notification.permission === "denied") this.blocked()
    this.show()
  }

  async subscribe() {
    this.enableTarget.disabled = true
    this.say(this.workingTextValue)
    try {
      const permission = await Notification.requestPermission()
      if (permission === "denied") return this.blocked()
      if (permission !== "granted") return this.say("") // prompt dismissed

      const registration = await navigator.serviceWorker.register(this.workerUrlValue, { scope: "/" })
      await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.applicationServerKey
      })
      await this.send("POST", { subscription: subscription.toJSON() })
      this.store(this.boundKeyValue, this.binding(subscription))
      this.enableTarget.classList.add("hidden")
      this.disableTarget.classList.remove("hidden")
      this.say(this.enabledTextValue)
    } catch (error) {
      console.warn("Push subscription failed:", error)
      this.say(this.failedTextValue)
    } finally {
      this.enableTarget.disabled = false
    }
  }

  async unsubscribe() {
    try {
      const subscription = await this.currentSubscription()
      if (subscription) {
        await subscription.unsubscribe()
        await this.send("DELETE", { endpoint: subscription.endpoint })
      }
      this.store(this.boundKeyValue, null)
      this.disableTarget.classList.add("hidden")
      this.enableTarget.classList.remove("hidden")
      this.say("")
    } catch (error) {
      console.warn("Push unsubscribe failed:", error)
      this.say(this.failedTextValue)
    }
  }

  dismiss() {
    this.store(this.dismissKeyValue, "1")
    this.hide()
  }

  // --- state -----------------------------------------------------------------

  get supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  // Safari on iOS/iPadOS outside a home-screen web app: no PushManager there.
  get iosBrowserTab() {
    const apple = /iP(hone|ad|od)/.test(navigator.platform) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    const standalone = window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true
    return apple && !standalone
  }

  get publicKey() {
    return document.querySelector("meta[name='web-push-public-key']")?.content
  }

  get dismissed() {
    return this.read(this.dismissKeyValue) === "1"
  }

  async currentSubscription() {
    try {
      const registration = await navigator.serviceWorker.getRegistration("/")
      return (await registration?.pushManager.getSubscription()) || null
    } catch (_) {
      return null
    }
  }

  // Re-post a subscription the server may not hold for this user: somebody
  // else signed in on this device, or the row was lost.
  async rebind(subscription) {
    const binding = this.binding(subscription)
    if (this.read(this.boundKeyValue) === binding) return
    try {
      await this.send("POST", { subscription: subscription.toJSON() })
      this.store(this.boundKeyValue, binding)
    } catch (error) {
      console.warn("Push subscription sync failed:", error)
    }
  }

  binding(subscription) {
    return `${this.userValue}:${subscription.endpoint}`
  }

  blocked() {
    this.enableTarget.classList.add("hidden")
    this.say(this.deniedTextValue)
  }

  // --- view ------------------------------------------------------------------

  show() {
    this.element.classList.remove("hidden")
  }

  hide() {
    this.element.classList.add("hidden")
  }

  say(text) {
    this.messageTarget.textContent = text || ""
    this.messageTarget.classList.toggle("hidden", !text)
  }

  // --- transport & storage ---------------------------------------------------

  async send(method, body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch(this.subscribeUrlValue, {
      method,
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token || "" },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
  }

  // The VAPID public key is base64url (padded); PushManager wants raw bytes.
  get applicationServerKey() {
    const base64 = this.publicKey.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")
    return Uint8Array.from(atob(padded), c => c.charCodeAt(0))
  }

  read(key) {
    try {
      return window.localStorage.getItem(key)
    } catch (_) {
      return null
    }
  }

  store(key, value) {
    try {
      if (value === null) window.localStorage.removeItem(key)
      else window.localStorage.setItem(key, value)
    } catch (_) {
      // private mode: nothing to remember
    }
  }
}
